import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Camera, Globe, BarChart3, Instagram, Users, Heart, SmartphoneNfc } from 'lucide-react';
import { useCmsContent } from '../../hooks/useCmsContent';

const MarketingStrategySection = () => {
  const { getContentValue, loading } = useCmsContent({ section: 'marketing' });
  
  const marketingServices = [
    {
      icon: Camera,
      title: "Servizio Fotografico Professionale",
      description: "Fotografie ad alta definizione che valorizzano ogni dettaglio del tuo immobile"
    },
    {
      icon: SmartphoneNfc,
      title: "Tour Virtuale 3D",
      description: "Esperienza immersiva che permette ai potenziali acquirenti di visitare l'immobile da remoto"
    },
    {
      icon: Globe,
      title: "Portali Immobiliari Premium",
      description: "Pubblicazione con annunci in evidenza sui maggiori portali nazionali e internazionali"
    },
    {
      icon: Instagram,
      title: "Social Media Marketing",
      description: "Campagne mirate su Instagram e Facebook per raggiungere potenziali acquirenti"
    },
    {
      icon: Users,
      title: "Database Acquirenti",
      description: "Presentazione del tuo immobile alla nostra esclusiva rete di contatti interessati"
    },
    {
      icon: BarChart3,
      title: "Analisi di Performance",
      description: "Monitoraggio continuo dell'interesse e adattamento della strategia in tempo reale"
    }
  ];

  return (
    <section className="py-24 bg-gray-50 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
        <img 
          src="https://images.unsplash.com/photo-1557682250-f37ae611f8ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
          alt="" 
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mb-16">
          <span className="text-secondary uppercase tracking-wider font-semibold">Strategie Avanzate</span>
          <h2 className="text-4xl font-monomakh text-primary mt-2 mb-6">
            Piano di Marketing <br />Personalizzato
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Sviluppiamo un piano di marketing su misura per il tuo immobile, 
            combinando tecniche tradizionali e digitali per massimizzare la visibilità 
            e attrarre gli acquirenti giusti.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {marketingServices.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="bg-primary/5 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <service.icon className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">{service.title}</h3>
              <p className="text-gray-600">{service.description}</p>
            </motion.div>
          ))}
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-20 bg-primary rounded-xl overflow-hidden shadow-xl"
        >
          <div className="grid md:grid-cols-2 items-center">
            <div className="p-10 text-white">
              <h3 className="text-3xl font-monomakh mb-6">Risultati Tangibili</h3>
              <p className="text-gray-200 mb-8 text-lg">
                Il nostro approccio orientato ai risultati ci permette di raggiungere obiettivi concreti:
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-4xl font-bold text-secondary mb-2">{getContentValue('visualizzazioni_percentuale') || "+65%"}</div>
                  <p className="text-gray-300">Visualizzazioni rispetto alle agenzie tradizionali</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-secondary mb-2">{getContentValue('percentuale_vendite_prezzo') || "92%"}</div>
                  <p className="text-gray-300">Delle vendite concluse al prezzo richiesto</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-secondary mb-2">{getContentValue('riduzione_tempi_vendita') || "-45%"}</div>
                  <p className="text-gray-300">Tempi di vendita ridotti rispetto alla media</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-secondary mb-2">{getContentValue('acquirenti_database') || "500+"}+</div>
                  <p className="text-gray-300">Potenziali acquirenti nel nostro database</p>
                </div>
              </div>
            </div>
            <div className="hidden md:block h-full">
              <img 
                src="/images/federico.jpg" 
                alt="Federico Molinaro"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MarketingStrategySection;